import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, X } from "lucide-react";

// Update this to your Cal.com handle (e.g. "obi-anayo/15min")
const CAL_LINK = "https://calendar.app.google/iXZfcWqTPkusBQsi7";

export default function FloatingBookCall() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("book_call_dismissed") === "1") {
      setDismissed(true);
      return;
    }
    const onScroll = () => {
      // appear after the user scrolls past the hero (~600px)
      setShow(window.scrollY > 600);
      // auto-expand once after a few seconds on screen, only the first time
      if (
        window.scrollY > 1200 &&
        !sessionStorage.getItem("book_call_seen")
      ) {
        setExpanded(true);
        sessionStorage.setItem("book_call_seen", "1");
        setTimeout(() => setExpanded(false), 4500);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("book_call_dismissed", "1");
  };

  if (dismissed) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.9 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          data-testid="floating-book-call"
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40"
        >
          <div className="relative group">
            {/* Pulse halo */}
            <span className="absolute inset-0 rounded-full bg-brand-500/30 animate-ping pointer-events-none"></span>

            {/* Dismiss button */}
            <button
              onClick={dismiss}
              data-testid="floating-book-call-dismiss"
              aria-label="Dismiss"
              className="absolute -top-2 -left-2 z-10 h-6 w-6 rounded-full bg-zinc-900 text-white border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>

            <a
              href={CAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="floating-book-call-btn"
              onMouseEnter={() => setExpanded(true)}
              onMouseLeave={() => setExpanded(false)}
              className="relative inline-flex items-center gap-2 bg-zinc-950 text-white pl-3 pr-4 py-3 rounded-full shadow-[0_12px_40px_-8px_rgba(99,91,255,0.55)] ring-1 ring-brand-500/40 hover:bg-brand-600 transition-colors"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full brand-gradient text-white">
                <Calendar className="h-3.5 w-3.5" />
              </span>
              <AnimatePresence initial={false}>
                {expanded ? (
                  <motion.span
                    key="long"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden whitespace-nowrap text-sm font-medium"
                  >
                    Book a 15-min intro call
                  </motion.span>
                ) : (
                  <motion.span
                    key="short"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-sm font-medium whitespace-nowrap"
                  >
                    Book a call
                  </motion.span>
                )}
              </AnimatePresence>
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-400"></span>
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
